import { useDispatch, useSelector } from "react-redux";
import { Outlet } from "react-router-dom";

// material-ui
import { styled, useTheme } from "@mui/material/styles";
import { AppBar, Box, CssBaseline, Toolbar, useMediaQuery } from "@mui/material";

// project imports
import Breadcrumbs from "../components/extended/Breadcrumbs";
import Header from "./Header/Header.js";
import LeftDrawer from "./LeftDrawer/LeftDrawer";
import Customization from "./Customization";
import SnackBarAlert from "../components/SnackBarAlert";
import menuItems from "../menu-items/menuItems.js";
import { drawerWidth } from "../store/constant.js";
import { SET_MENU, CLEAR_GLOBAL_ALERT } from "../store/actions.js";

// assets
import { IconChevronRight } from "@tabler/icons";

// styles
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
    ({ theme, open }) => ({
        ...theme.typography.mainContent,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        transition: theme.transitions.create(
            "margin",
            open
                ? {
                      easing: theme.transitions.easing.easeOut,
                      duration: theme.transitions.duration.enteringScreen,
                  }
                : {
                      easing: theme.transitions.easing.sharp,
                      duration: theme.transitions.duration.leavingScreen,
                  }
        ),
        [theme.breakpoints.up("md")]: {
            marginLeft: open ? 0 : -(drawerWidth - 20),
            width: `calc(100% - ${drawerWidth}px)`,
        },
        [theme.breakpoints.down("md")]: {
            marginLeft: "20px",
            width: `calc(100% - ${drawerWidth}px)`,
            padding: "16px",
        },
        [theme.breakpoints.down("sm")]: {
            marginLeft: "10px",
            width: `calc(100% - ${drawerWidth}px)`,
            padding: "16px",
            marginRight: "10px",
        },
    })
);

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();
    const matchDownMd = useMediaQuery(theme.breakpoints.down("md"));
    // Handle left drawer
    const leftDrawerOpened = useSelector((state) => state.customization.opened);
    const globalAlert = useSelector((state) => state.customization.globalAlert);
    const dispatch = useDispatch();
    const handleLeftDrawerToggle = () => {
        dispatch({ type: SET_MENU, opened: !leftDrawerOpened });
    };

    const handleCloseAlert = (event, reason) => {
        if (reason === "clickaway") return;
        dispatch({ type: CLEAR_GLOBAL_ALERT });
    };

    return (
        <Box sx={{ display: "flex" }}>
            <CssBaseline />
            {/* header */}
            <AppBar
                enableColorOnDark
                position="fixed"
                color="inherit"
                elevation={0}
                sx={{
                    bgcolor: theme.palette.background.default,
                    transition: leftDrawerOpened ? theme.transitions.create("width") : "none",
                }}
            >
                <Toolbar>
                    <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
                </Toolbar>
            </AppBar>

            {/* drawer */}
            <LeftDrawer
                drawerOpen={!matchDownMd ? leftDrawerOpened : !leftDrawerOpened}
                drawerToggle={handleLeftDrawerToggle}
            />

            {/* main content */}
            <Main theme={theme} open={leftDrawerOpened}>
                {/* breadcrumb */}
                <Breadcrumbs
                    separator={IconChevronRight}
                    menuItems={menuItems}
                    icon
                    title
                    rightAlign
                />
                <Outlet />
            </Main>
            <Customization />
            {globalAlert && (
                <SnackBarAlert
                    open={globalAlert.open}
                    handleClose={handleCloseAlert}
                    message={globalAlert.message}
                    severity={globalAlert.severity}
                />
            )}
        </Box>
    );
};

export default MainLayout;
